import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDownRight } from "lucide-react";
import { SITE } from "@/lib/site";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG =
  "https://images.pexels.com/photos/9806482/pexels-photo-9806482.jpeg?auto=compress&cs=tinysrgb&w=1100&h=1400";

const LINES = ["Wholesale merchandise,", "sourced and shipped", "worldwide."];

const Hero = () => {
  const { scrollY } = useScroll();
  const parallaxY = useTransform(scrollY, [0, 700], [0, 90]);

  return (
    <section className="relative pt-[72px]" data-testid="hero-section">
      <div className="max-w-[1240px] mx-auto px-5 md:px-8 min-h-[calc(100vh-72px)] grid lg:grid-cols-[1.12fr_0.88fr] gap-12 lg:gap-16 items-center py-12 lg:py-0">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-mono text-[11px] md:text-[12px] tracking-[0.3em] uppercase text-accent"
            data-testid="hero-eyebrow"
          >
            Wholesale · Import / Export · Pembroke Pines, FL
          </motion.p>

          <h1
            className="mt-6 font-display font-extrabold text-ink tracking-[-0.025em] leading-[0.99] text-[clamp(2.7rem,7vw,5.6rem)]"
            data-testid="hero-heading"
          >
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "text-accent" : ""}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.18 + i * 0.13, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="mt-7 max-w-xl text-[17px] leading-relaxed text-slate-600"
            data-testid="hero-subline"
          >
            {SITE.name} sources and coordinates wholesale orders — overstock goods, shelf
            pulls, perishables, and general merchandise — for Amazon sellers, resellers,
            and buyers shipping overseas.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <a
              href={SITE.phoneHref}
              data-testid="hero-call-button"
              className="inline-flex items-center justify-center gap-2.5 rounded-full bg-accent hover:bg-accentDeep text-white font-semibold px-7 py-4 text-[15px] transition-colors duration-200"
            >
              <Phone size={17} strokeWidth={2.2} />
              Call {SITE.phoneDisplay}
            </a>
            <button
              type="button"
              data-testid="hero-inquiry-button"
              onClick={() => scrollToId("contact")}
              className="group inline-flex items-center justify-center gap-2.5 rounded-full border border-slate-300 hover:border-ink bg-white text-ink font-semibold px-7 py-4 text-[15px] transition-colors duration-200"
            >
              Send an inquiry
              <ArrowDownRight
                size={17}
                className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:translate-y-0.5"
              />
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-8 font-mono text-[11px] tracking-[0.22em] uppercase text-slate-400"
            data-testid="hero-availability-note"
          >
            Call for availability and pricing — products and quantities vary
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <div
            className="relative h-[340px] sm:h-[440px] lg:h-[64vh] lg:min-h-[480px] rounded-2xl overflow-hidden"
            data-testid="hero-image-frame"
          >
            <motion.img
              src={HERO_IMG}
              alt="Cargo ship loaded with shipping containers at sea"
              style={{ y: parallaxY }}
              className="absolute inset-0 w-full h-[118%] object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-ink/15" aria-hidden="true" />
          </div>

          <div
            className="hidden md:block absolute -left-8 bottom-10 bg-white border border-slate-200 rounded-xl p-5 w-[290px]"
            data-testid="hero-manifest-card"
          >
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-400">
              Order file
            </p>
            <dl className="mt-4 space-y-2.5 text-[13px]">
              {[
                ["Shipper", "Eli Capital Investments"],
                ["Goods", "Merchandise, varied lots"],
                ["Origin", "Pembroke Pines, FL"],
                ["Destination", "Worldwide"],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4">
                  <dt className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-400 pt-0.5">
                    {k}
                  </dt>
                  <dd className="text-right font-medium text-ink">{v}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-4 h-6 barcode-strip rounded-sm" aria-hidden="true" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
